import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Calendar, Clock, Users, CreditCard, Check, AlertCircle, Plus, Minus, MapPin, Phone, ArrowLeft, Navigation, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SEOHead from '../components/SEOHead';

const BookTourPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [timeSlots, setTimeSlots] = useState({});
  const [isDateBlocked, setIsDateBlocked] = useState(false);
  const [blackoutReason, setBlackoutReason] = useState('');
  const [tickets, setTickets] = useState({
    adult: 0,
    child: 0
  });
  const [selectedPackages, setSelectedPackages] = useState({
    family: [],
    custom: []
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    visitingFrom: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(true);
  const [showRefundModal, setShowRefundModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tourBookingEnabled, setTourBookingEnabled] = useState(true);
  const [pricing, setPricing] = useState({
    adult: 15.50,
    child: 8.50,
    family_packages: [],
    custom_packages: []
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Underground Mine Tour Booking",
    "description": "Book your underground opal mine tour at Umoona Opal Mine, Coober Pedy. Experience authentic opal mining with professional guides.",
    "url": "https://umoonaopalmine.com.au/book-tour"
  };

  useEffect(() => {
    fetchPricing();
    fetchFeatureSettings();
  }, []);

  // Live chat widget
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://chat.turbo.net.au/widget.js';
    script.setAttribute('data-site-id', '1de29108-04e3-4a90-a55e-f61dba1f8c7d');
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchFeatureSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/features/public`);
      if (response.ok) {
        const data = await response.json();
        setTourBookingEnabled(data.tour_booking_enabled !== false);
      }
    } catch (error) {
      console.error('Error fetching feature settings:', error);
      // Default to enabled on error
      setTourBookingEnabled(true);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tours/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const checkAvailability = useCallback(async (date) => {
    if (!date) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tours/availability/${date}`);
      if (response.ok) {
        const data = await response.json();
        if (data.blocked) {
          setIsDateBlocked(true);
          setBlackoutReason(data.blackout_reason || '');
          setAvailableTimes([]);
          setTimeSlots({});
          setSelectedTime('');
        } else {
          setIsDateBlocked(false);
          setBlackoutReason('');
          // Use the available times from the API (it already respects the tour schedule settings)
          const apiTimes = data.available_times || ['10:00', '14:00', '16:00'];
          setAvailableTimes(apiTimes);
          setTimeSlots(data.time_slots || {});
          // Clear the selected time if it's no longer offered on this date
          // (e.g. switching to a Mon/Fri that only runs 4pm, or a blacked-out slot)
          // or if it's now sold out. Otherwise a stale selection could be submitted.
          setSelectedTime(prev => {
            if (!prev) return prev;
            if (!apiTimes.includes(prev)) return '';
            const slot = data.time_slots ? data.time_slots[prev] : null;
            if (slot && slot.total_remaining <= 0) return '';
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      // Default to all times on error
      setAvailableTimes(['10:00', '14:00', '16:00']);
      setIsDateBlocked(false);
      setBlackoutReason('');
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      checkAvailability(selectedDate);
    }
  }, [selectedDate, checkAvailability]);

  const getRemainingCapacity = () => {
    if (!selectedTime || !timeSlots[selectedTime]) return 30;
    return timeSlots[selectedTime].total_remaining;
  };

  const updateTicketQuantity = (ticketType, change) => {
    if (change > 0 && getTotalGuests() >= getRemainingCapacity()) return;
    setTickets(prev => ({
      ...prev,
      [ticketType]: Math.max(0, prev[ticketType] + change)
    }));
  };

  const BOOKING_FEE_PERCENT = 2.8;

  const calculateSubtotal = () => {
    let subtotal = (tickets.adult * pricing.adult) + (tickets.child * pricing.child);

    // Add family package prices
    selectedPackages.family.forEach(pkg => {
      subtotal += pkg.price * pkg.quantity;
    });

    // Add custom package prices
    selectedPackages.custom.forEach(pkg => {
      subtotal += pkg.price * pkg.quantity;
    });

    return subtotal;
  };

  const calculateBookingFee = () => {
    return calculateSubtotal() * (BOOKING_FEE_PERCENT / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateBookingFee();
  };

  const getTotalGuests = () => {
    let guests = tickets.adult + tickets.child;
    
    // Add guests from family packages using the guest_count field
    selectedPackages.family.forEach(pkg => {
      if (pkg.guest_count) {
        guests += pkg.guest_count * pkg.quantity;
      }
    });
    
    return guests;
  };

  const addPackage = (type, packageData) => {
    const guestsInPackage = packageData.guest_count || parseInt(packageData.name?.match(/\d+/)?.[0]) || 1;
    if (getTotalGuests() + guestsInPackage > getRemainingCapacity()) return;
    setSelectedPackages(prev => {
      const packages = [...prev[type]];
      const existingIndex = packages.findIndex(p => p.name === packageData.name);
      
      if (existingIndex >= 0) {
        packages[existingIndex].quantity += 1;
      } else {
        packages.push({ ...packageData, quantity: 1 });
      }
      
      return { ...prev, [type]: packages };
    });
  };

  const removePackage = (type, packageName) => {
    setSelectedPackages(prev => {
      const packages = [...prev[type]];
      const existingIndex = packages.findIndex(p => p.name === packageName);
      
      if (existingIndex >= 0) {
        if (packages[existingIndex].quantity > 1) {
          packages[existingIndex].quantity -= 1;
        } else {
          packages.splice(existingIndex, 1);
        }
      }
      
      return { ...prev, [type]: packages };
    });
  };

  const getPackageQuantity = (type, packageName) => {
    const pkg = selectedPackages[type].find(p => p.name === packageName);
    return pkg ? pkg.quantity : 0;
  };

  const formatTimeDisplay = (time) => {
    const hour = parseInt(time.split(':')[0]);
    if (hour === 10) return '10:00 AM';
    if (hour === 14) return '2:00 PM'; 
    if (hour === 16) return '4:00 PM';
    return time;
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || getTotalGuests() === 0) {
      alert('Please select date, time, and number of tickets');
      return;
    }

    if (!customerInfo.name || !customerInfo.email) {
      alert('Please fill in your name and email address');
      return;
    }

    if (!termsAccepted) {
      alert('Please accept the Terms & Conditions to proceed with your booking');
      return;
    }

    setLoading(true);
    try {
      // Build tickets array with individual tickets
      const ticketsArray = [
        { ticket_type: 'adult', quantity: tickets.adult, price_per_ticket: pricing.adult },
        { ticket_type: 'child', quantity: tickets.child, price_per_ticket: pricing.child }
      ].filter(t => t.quantity > 0);
      
      // Add family packages as ticket entries - map package name to ticket_type
      selectedPackages.family.forEach(pkg => {
        // Map package name to backend ticket_type enum
        let ticketType = 'adult'; // fallback
        const guestCount = pkg.guests || parseInt(pkg.name.match(/\d+/)?.[0]) || 0;

        if (guestCount === 3 || pkg.name.includes('3')) {
          ticketType = 'family_3';
        } else if (guestCount === 4 || pkg.name.includes('4')) {
          ticketType = 'family_4';
        } else if (guestCount === 5 || pkg.name.includes('5')) {
          ticketType = 'family_5';
        } else if (guestCount === 6 || pkg.name.includes('6')) {
          ticketType = 'family_6';
        } else if (guestCount === 7 || pkg.name.includes('7')) {
          ticketType = 'family_7';
        } else if (guestCount === 8 || pkg.name.includes('8')) {
          ticketType = 'family_8';
        } else if (pkg.ticket_type) {
          ticketType = pkg.ticket_type;
        }

        ticketsArray.push({
          ticket_type: ticketType,
          quantity: pkg.quantity || 1,
          price_per_ticket: pkg.price
        });
      });

      // Add custom packages - these need to be broken down into adult tickets
      selectedPackages.custom.forEach(pkg => {
        // Custom packages are converted to adult tickets for simplicity
        ticketsArray.push({
          ticket_type: 'adult',
          quantity: pkg.guests || 1,
          price_per_ticket: pkg.price / (pkg.guests || 1)
        });
      });
      
      const bookingData = {
        tour_date: selectedDate,
        tour_time: selectedTime,
        tickets: ticketsArray,
        customer: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          visiting_from: customerInfo.visitingFrom
        },
        subtotal: calculateSubtotal(),
        booking_fee: calculateBookingFee(),
        booking_fee_percent: BOOKING_FEE_PERCENT,
        total_amount: calculateTotal(),
        guest_count: getTotalGuests()
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tours/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json();
        // Handle different error formats
        let errorMessage = 'Booking failed';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Booking failed: ' + (error.message || 'Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  // Show disabled state if tour booking is not enabled
  if (!tourBookingEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50">
        <SEOHead
          title="Tour Bookings - Umoona Opal Mine"
          description="Underground mine tour information for Umoona Opal Mine, Coober Pedy"
        />
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="bg-white rounded-2xl shadow-xl p-12">
              <div className="mb-6">
                <Calendar className="w-24 h-24 mx-auto text-blue-300" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Online Tour Bookings Unavailable
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Our online tour booking system is temporarily unavailable.
              </p>
              <Alert className="border-green-200 bg-green-50 mb-8">
                <AlertCircle className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-800 text-left">
                  <strong className="block mb-3 text-xl">Don't worry! You can still join our underground tours.</strong>
                  <p className="mb-3">Simply visit our opal shop <strong>10 minutes before tour time</strong> to purchase tickets in person from our friendly staff.</p>
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <p className="font-semibold mb-2">Daily Tour Times:</p>
                    <p className="mb-1">• 10:00 AM</p>
                    <p className="mb-1">• 2:00 PM</p>
                    <p className="mb-3">• 4:00 PM</p>
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <p className="font-semibold mb-2">Location:</p>
                      <p className="mb-1">Umoona Opal Mine & Museum</p>
                      <p className="mb-1">14 Hutchison St, Coober Pedy SA 5723</p>
                      <p><strong>Phone:</strong> (08) 8672 5288</p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Link to="/">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/contact">
                    <Navigation className="w-4 h-4 mr-2" />
                    Get Directions
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <SEOHead
        title="Book Underground Mine Tour - Umoona Opal Mine Tickets"
        description="Book your underground opal mine tour at Umoona Opal Mine, Coober Pedy. Experience authentic opal mining with professional guides. Adult $15.50, Child $8.50."
        keywords="book opal mine tour, underground tour tickets, Coober Pedy tour booking, mine tour reservation"
        canonical="/book-tour"
        schemaData={schemaData}
      />

      {/* Non-Refundable Acknowledgement Modal */}
      {showRefundModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[9999]" />
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
              <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
                <AlertCircle className="w-7 h-7 text-white" />
                <h2 className="text-xl font-bold text-white">Important Notice</h2>
              </div>
              <div className="px-6 py-6 space-y-4">
                <p className="text-gray-800 text-base leading-relaxed">
                  All online ticket sales are final. Tickets are non-refundable and cannot be exchanged, transferred, or rescheduled to a different date or time.
                </p>
                <p className="text-gray-800 text-base leading-relaxed">
                  If you are unsure of travel dates or tour times, you can visit our opal shop on the day you arrive to check availability.
                </p>
              </div>
              <div className="px-6 pb-6 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 py-3 text-base font-semibold"
                  onClick={() => window.location.href = '/'}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 py-3 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setShowRefundModal(false)}
                >
                  Acknowledge
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Hero Section */}
      <section className="py-8 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            Book Underground Mine Tour
          </h1>
          <p className="text-base text-slate-300 mb-5">
            Reserve your authentic underground opal mine experience in Coober Pedy
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            <span className="bg-white/10 text-white px-3 py-1.5 rounded-full border border-white/15">Adults ${pricing.adult.toFixed(2)}</span>
            <span className="bg-white/10 text-white px-3 py-1.5 rounded-full border border-white/15">Children ${pricing.child.toFixed(2)}</span>
            <span className="bg-white/10 text-white px-3 py-1.5 rounded-full border border-white/15">Tours: 10am, 2pm, 4pm</span>
          </div>
          <p className="mt-4 text-sm text-amber-300">
            10% off opal jewellery with online bookings <span className="text-slate-400 text-xs ml-1">(excludes watches, loose stones & sale items)</span>
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Tour Booking Disabled Message */}
          {!tourBookingEnabled && (
            <Alert className="mb-8 border-orange-200 bg-orange-50">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <AlertDescription className="text-orange-800 text-lg leading-relaxed">
                <strong className="block mb-2 text-xl">Sorry, online ticket bookings are currently unavailable.</strong>
                <p className="mb-0">
                  Don't stress. You can still explore the underground. Simply pop in and see our shop staff at Umoona Opal Mine 
                  10 minutes prior to a tour time to book your tickets in person.
                </p>
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <p className="font-semibold mb-2">Tour Times:</p>
                  <p className="mb-1">Daily: 10:00 AM, 2:00 PM, 4:00 PM</p>
                  <p className="mt-3 text-sm">
                    <strong>Location:</strong> 14 Hutchison St, Coober Pedy SA 5723<br />
                    <strong>Phone:</strong> (08) 8672 5288
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {tourBookingEnabled && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Main Booking Form */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Step 1: Select Date */}
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
                  <CardTitle className="flex items-center text-xl">
                    <Calendar className="w-6 h-6 mr-3" />
                    Step 1: Select Date
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={today}
                      className="text-lg p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500"
                    />
                    
                    {isDateBlocked && (
                      <Alert className="border-red-200 bg-red-50">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-800">
                          <strong>Tickets unavailable for this date{blackoutReason ? `: ${blackoutReason}` : ''}</strong>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Step 2: Select Time */}
              {selectedDate && !isDateBlocked && (
                <Card className="shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-t-lg">
                    <CardTitle className="flex items-center text-xl">
                      <Clock className="w-6 h-6 mr-3" />
                      Step 2: Select Tour Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {availableTimes.map((time) => {
                        const slotInfo = timeSlots[time];
                        const isSoldOut = slotInfo && slotInfo.total_remaining <= 0;
                        return (
                          <Button
                            key={time}
                            variant={selectedTime === time ? "default" : "outline"}
                            onClick={() => !isSoldOut && setSelectedTime(time)}
                            disabled={isSoldOut}
                            className={`p-6 h-auto text-lg font-semibold ${isSoldOut ? 'opacity-50 cursor-not-allowed' : ''}`}
                            size="lg"
                          >
                            {formatTimeDisplay(time)}
                            {isSoldOut && <span className="block text-sm text-red-500 font-bold mt-1">Sold Out</span>}
                          </Button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Step 3: Select Tickets */}
              {selectedTime && (
                <Card className="shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-lg">
                    <CardTitle className="flex items-center text-xl">
                      <Users className="w-6 h-6 mr-3" />
                      Step 3: Select Number of Tickets
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {getRemainingCapacity() < 30 && (
                      <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${getRemainingCapacity() <= 5 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        {getRemainingCapacity()} spot{getRemainingCapacity() !== 1 ? 's' : ''} remaining for this tour time
                      </div>
                    )}
                    <div className="space-y-6">

                      {/* Adults */}
                      <div className="flex items-center justify-between p-6 border-2 border-gray-200 rounded-xl hover:border-blue-300 transition-colors">
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900">Adults</h4>
                          <p className="text-gray-600">${pricing.adult.toFixed(2)} per person</p>
                          <p className="text-sm text-gray-500">Ages 16 and over</p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Button
                            size="lg"
                            variant="outline"
                            onClick={() => updateTicketQuantity('adult', -1)}
                            disabled={tickets.adult === 0}
                            className="w-12 h-12 rounded-full"
                          >
                            <Minus className="w-5 h-5" />
                          </Button>
                          <span className="w-12 text-center font-bold text-2xl text-purple-600">
                            {tickets.adult}
                          </span>
                          <Button
                            size="lg"
                            variant="outline"
                            onClick={() => updateTicketQuantity('adult', 1)}
                            disabled={getTotalGuests() >= getRemainingCapacity()}
                            className="w-12 h-12 rounded-full"
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="flex items-center justify-between p-6 border-2 border-gray-200 rounded-xl hover:border-green-300 transition-colors">
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900">Children</h4>
                          <p className="text-gray-600">${pricing.child.toFixed(2)} per child</p>
                          <p className="text-sm text-gray-500">Under 16 years old</p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Button
                            size="lg"
                            variant="outline"
                            onClick={() => updateTicketQuantity('child', -1)}
                            disabled={tickets.child === 0}
                            className="w-12 h-12 rounded-full"
                          >
                            <Minus className="w-5 h-5" />
                          </Button>
                          <span className="w-12 text-center font-bold text-2xl text-green-600">
                            {tickets.child}
                          </span>
                          <Button
                            size="lg"
                            variant="outline"
                            onClick={() => updateTicketQuantity('child', 1)}
                            disabled={getTotalGuests() >= getRemainingCapacity()}
                            className="w-12 h-12 rounded-full"
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Family Packages */}
                      {pricing.family_packages && pricing.family_packages.length > 0 && (
                        <div className="col-span-full">
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                            <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
                              <Users className="w-6 h-6 mr-2" />
                              Family Packages
                            </h3>
                            <div className="space-y-3">
                              {pricing.family_packages.map((pkg, index) => (
                                <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
                                  <div className="flex-1">
                                    <h4 className="font-bold text-gray-900">{pkg.name}</h4>
                                    <p className="text-sm text-gray-600">{pkg.description}</p>
                                    <p className="text-lg font-semibold text-green-600 mt-1">${pkg.price.toFixed(2)}</p>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => removePackage('family', pkg.name)}
                                      disabled={getPackageQuantity('family', pkg.name) === 0}
                                      className="w-10 h-10 rounded-full"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </Button>
                                    <span className="w-8 text-center font-bold text-xl text-green-600">
                                      {getPackageQuantity('family', pkg.name)}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => addPackage('family', pkg)}
                                      disabled={getTotalGuests() >= getRemainingCapacity()}
                                      className="w-10 h-10 rounded-full"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Custom Packages */}
                      {pricing.custom_packages && pricing.custom_packages.length > 0 && (
                        <div className="col-span-full">
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-200">
                            <h3 className="text-xl font-bold text-amber-900 mb-4 flex items-center">
                              <CreditCard className="w-6 h-6 mr-2" />
                              Special Offers
                            </h3>
                            <div className="space-y-3">
                              {pricing.custom_packages.map((pkg, index) => (
                                <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200">
                                  <div className="flex-1">
                                    <h4 className="font-bold text-gray-900">{pkg.name}</h4>
                                    <p className="text-sm text-gray-600">{pkg.description}</p>
                                    <p className="text-lg font-semibold text-amber-600 mt-1">${pkg.price.toFixed(2)}</p>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => removePackage('custom', pkg.name)}
                                      disabled={getPackageQuantity('custom', pkg.name) === 0}
                                      className="w-10 h-10 rounded-full"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </Button>
                                    <span className="w-8 text-center font-bold text-xl text-amber-600">
                                      {getPackageQuantity('custom', pkg.name)}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => addPackage('custom', pkg)}
                                      disabled={getTotalGuests() >= getRemainingCapacity()}
                                      className="w-10 h-10 rounded-full"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Step 4: Customer Information */}
              {getTotalGuests() > 0 && (
                <Card className="shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-t-lg">
                    <CardTitle className="text-xl">Customer Information</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          placeholder="Enter your full name"
                          value={customerInfo.name}
                          onChange={(e) => setCustomerInfo(prev => ({...prev, name: e.target.value}))}
                          className="text-lg p-3"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={customerInfo.email}
                          onChange={(e) => setCustomerInfo(prev => ({...prev, email: e.target.value}))}
                          className="text-lg p-3"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          placeholder="Enter your phone number"
                          value={customerInfo.phone}
                          onChange={(e) => setCustomerInfo(prev => ({...prev, phone: e.target.value}))}
                          className="text-lg p-3"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Where are you visiting from?
                        </label>
                        <Input
                          type="text"
                          placeholder="e.g., Sydney, London, Tokyo"
                          value={customerInfo.visitingFrom}
                          onChange={(e) => setCustomerInfo(prev => ({...prev, visitingFrom: e.target.value}))}
                          className="text-lg p-3"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Terms & Conditions */}
              {getTotalGuests() > 0 && customerInfo.name && customerInfo.email && (
                <Card className="shadow-lg border-2 border-amber-200">
                  <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg py-4">
                    <CardTitle className="text-lg">Terms & Conditions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {/* Expandable Terms */}
                      <div className="bg-gray-50 rounded-lg border border-gray-200">
                        <button
                          type="button"
                          onClick={() => setShowTerms(!showTerms)}
                          className="w-full px-4 py-3 flex items-center justify-between text-left font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <span>View Full Terms & Conditions</span>
                          <span className={`transform transition-transform ${showTerms ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </button>

                        {showTerms && (
                          <div className="px-4 pb-4 text-sm text-gray-600 space-y-4 border-t border-gray-200 pt-4">
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-1">Tickets & Refunds</h4>
                              <p>All ticket sales are final. Tickets are non-refundable and <strong>cannot</strong> be exchanged, transferred, or rescheduled to a different date or time.</p>
                            </div>

                            <div>
                              <h4 className="font-semibold text-gray-800 mb-1">Tour Participation</h4>
                              <p>Guests must arrive at least 10-15 minutes prior to their scheduled tour time. Late arrivals may not be permitted and will not be entitled to a refund. Participants must follow all safety instructions provided by tour guides. Umoona Opal Mine reserves the right to refuse entry to any guest who does not comply with safety requirements or whose behaviour poses a risk to themselves or others.</p>
                            </div>

                            <div>
                              <h4 className="font-semibold text-gray-800 mb-1">Tour Modifications</h4>
                              <p>Umoona Opal Mine reserves the right to modify, cancel, or reschedule tours due to unforeseen circumstances, safety concerns, or operational requirements. In the event of a cancellation initiated by Umoona Opal Mine, guests will be offered an alternative tour time or a full refund.</p>
                            </div>

                            <div>
                              <h4 className="font-semibold text-gray-800 mb-1">Liability</h4>
                              <p>Guests participate in the underground tour at their own risk. Umoona Opal Mine accepts no responsibility for loss, injury, or damage to personal belongings.</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Checkbox */}
                      <label className="flex items-start space-x-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="mt-1 h-5 w-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">
                          I have read, understood, and accepted the{' '}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                            className="text-amber-600 hover:text-amber-700 underline font-medium"
                          >
                            Terms & Conditions
                          </button>
                          {' '}<span className="text-red-500">*</span>
                        </span>
                      </label>

                      {!termsAccepted && (
                        <p className="text-xs text-amber-600">Please accept the terms and conditions to proceed with your booking.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Booking Summary Sidebar */}
            <div className="space-y-6">
              <Card className="sticky top-24 shadow-lg z-10">
                <CardHeader className="bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-t-lg">
                  <CardTitle className="text-xl">Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Date & Time */}
                    {selectedDate && (
                      <div className="space-y-3 pb-4 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">Date:</span>
                          <span className="font-semibold text-gray-900">
                            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-AU', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long', 
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        {selectedTime && (
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-700">Time:</span>
                            <span className="font-semibold text-gray-900">{formatTimeDisplay(selectedTime)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Tickets */}
                    {getTotalGuests() > 0 && (
                      <div className="space-y-3 pb-4 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900">Tickets:</h4>

                        {tickets.adult > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-700">{tickets.adult} Adult{tickets.adult > 1 ? 's' : ''}</span>
                            <span className="font-medium">${(tickets.adult * pricing.adult).toFixed(2)}</span>
                          </div>
                        )}

                        {tickets.child > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-700">{tickets.child} Child{tickets.child > 1 ? 'ren' : ''}</span>
                            <span className="font-medium">${(tickets.child * pricing.child).toFixed(2)}</span>
                          </div>
                        )}

                        {/* Family Packages */}
                        {selectedPackages.family.map((pkg, index) => (
                          <div key={`family-${index}`} className="flex justify-between">
                            <span className="text-gray-700">{pkg.name}</span>
                            <span className="font-medium">${pkg.price.toFixed(2)}</span>
                          </div>
                        ))}

                        {/* Custom Packages */}
                        {selectedPackages.custom.map((pkg, index) => (
                          <div key={`custom-${index}`} className="flex justify-between">
                            <span className="text-gray-700">{pkg.name}</span>
                            <span className="font-medium">${pkg.price.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Total */}
                    {getTotalGuests() > 0 && (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center text-lg">
                          <span className="font-medium text-gray-700">Total Guests:</span>
                          <span className="font-semibold text-gray-900">{getTotalGuests()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Subtotal:</span>
                          <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Booking Fee ({BOOKING_FEE_PERCENT}%):</span>
                          <span className="text-gray-600">${calculateBookingFee().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xl bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
                          <span className="font-bold text-gray-900">Total Price:</span>
                          <span className="font-bold text-2xl text-purple-600">${calculateTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* Book Button */}
                    {getTotalGuests() > 0 && customerInfo.name && customerInfo.email && (
                      <div className="mt-6">
                        <Button
                          onClick={handleBooking}
                          disabled={loading || !termsAccepted}
                          className={`w-full text-lg py-4 ${termsAccepted ? 'mining-cta' : 'bg-gray-400 cursor-not-allowed'}`}
                          size="lg"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              Book Tour - ${calculateTotal().toFixed(2)}
                              <CreditCard className="ml-2 w-5 h-5" />
                            </>
                          )}
                        </Button>
                        {!termsAccepted && (
                          <p className="text-center text-sm text-amber-600 mt-2">Please accept the Terms & Conditions above to proceed</p>
                        )}
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                      <h4 className="font-semibold text-gray-900 mb-3">Need Help?</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <button
                          onClick={() => {
                            if (window.TurboChat) {
                              window.TurboChat.open();
                            }
                          }}
                          className="flex items-center justify-center w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Live Chat Support
                        </button>
                        <div className="flex items-center justify-center">
                          <Phone className="w-4 h-4 mr-2" />
                          <a href="tel:0886725288" className="text-blue-600 hover:text-blue-800">
                            (08) 8672 5288
                          </a>
                        </div>
                        <div className="flex items-center justify-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          <span>14 Hutchison St, Coober Pedy</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Important Information */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-blue-900 mb-6 text-center">Important Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">What to Bring:</h4>
                  <ul className="space-y-2 text-blue-700">
                    <li>• Comfortable closed-toe shoes (essential)</li>
                    <li>• Camera for photos</li>
                    <li>• Sense of adventure!</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">Tour Details:</h4>
                  <ul className="space-y-2 text-blue-700">
                    <li>• Tour duration: 60 minutes</li>
                    <li>• Underground temperature: 22-24°C</li>
                    <li>• 15 steep steps to enter mine</li>
                    <li>• Photography permitted</li>
                  </ul>
                </div>
              </div>

              <Alert className="mt-6 border-yellow-300 bg-yellow-100">
                <AlertCircle className="h-4 w-4 text-yellow-700" />
                <AlertDescription className="text-yellow-800">
                  <strong>Please arrive 10 minutes before your tour time</strong> to allow for check-in with our shop staff and safety briefings.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default BookTourPage;